/* @flow */
import { Observable } from 'rxjs';
import { prop, path } from 'ramda';
import { Map, fromJS } from 'immutable';
import { stringify } from 'querystring';
import { combineEpics, ActionsObservable } from 'redux-observable';
import { LOCATION_CHANGE } from 'react-router-redux';

import { MALWARE_ENDPOINT } from './constants';
import {
  UPDATE_PAGE_FILTERS,
  RESET_PAGE_FILTERS,
  FETCH_MALWARE,
  FETCH_MALWARE_SUCCESS,
  FETCH_MALWARE_FAILURE,
  SAVE_MALWARE,
  SAVE_MALWARE_SUCCESS,
  SAVE_MALWARE_FAILURE,
  DELETE_MALWARE,
  DELETE_MALWARE_SUCCESS,
  DELETE_MALWARE_FAILURE,
} from './action-types';
import * as getters from './getters.js';
import { error } from 'modules/alerts/actions';
import { normalizeMalware } from './utils';

const formatSearchRequest = (payload: Map<string, any>) => {
  const sort = payload.getIn(['sort', 'sort'], {});
  const params = {
    'query': payload.get('query', ''),
    'sort': sort.sortBy + ',' + sort.sortDirection,
    'pageNumber': payload.get('pageNumber'),
    'pageSize': payload.get('pageSize'),
  }
  return stringify(params);
};

const viewLoadedEpic = (action$: ActionsObservable<*>) =>
  action$.ofType(LOCATION_CHANGE)
    .map(prop('payload'))
    .filter((x) => {
      return x.pathname === '/settings/malware';
    })
    .mergeMap(() => Observable.from([{ type: FETCH_MALWARE }]));

/* Fetches all Malware terms and aliases */
const fetchMalwareEpic = (action$, store, { ajax }) =>
  action$.ofType(FETCH_MALWARE, UPDATE_PAGE_FILTERS, RESET_PAGE_FILTERS)
    .mergeMap(() => {
      const currentQuery = getters.currentQuery(store.getState());
      return ajax.get(MALWARE_ENDPOINT + '?' + formatSearchRequest(currentQuery))
        .map(prop('response'))
        .map(response => ({
          type: FETCH_MALWARE_SUCCESS,
          payload: Map(response),
        }))
        .catch(err => Observable.of({ type: FETCH_MALWARE_FAILURE }));
    });

/* Adds a new Malware term and aliases */
const saveMalwareEpic = (action$, store, { ajax }) =>
  action$.ofType(SAVE_MALWARE)
    .mergeMap(({ payload }) => {
      const saveType = payload.get('guid') ? 'updated' : 'saved';
      const successMessage = `The Malware term ${payload.get('name')} was ${saveType} successfully.`;
      return ajax.postJSON(MALWARE_ENDPOINT, payload.toJS())
        .map(prop('response'))
        .map(malware => ({
          type: SAVE_MALWARE_SUCCESS,
          payload: normalizeMalware(fromJS(malware)),
          meta: {
            alert: { type: 'success', text: successMessage },
          },
        }))
        .catch(err => {
          const serverError = path(['xhr', 'response', 'message'], err);
          const message = serverError
            ? serverError
            : 'There was an error adding the Malware term.';
          return Observable.from([
            { type: SAVE_MALWARE_FAILURE },
            error(message),
          ]);
        });
    });

/* Removes a Malware Term */
const deleteMalwareEpic = (action$, store, { ajax }) =>
  action$.ofType(DELETE_MALWARE)
    .mergeMap(({ payload }) => {
      const guid = payload.get('guid');
      const name = payload.get('name');
      return ajax.delete(`${MALWARE_ENDPOINT}/${guid}`)
        .map(() => ({
          type: DELETE_MALWARE_SUCCESS,
          payload: { guid },
          meta: {
            alert: { type: 'success', text: `The Malware term ${name} was deleted successfully.` },
          },
        }))
        .catch(err => {
          return Observable.from([
            { type: DELETE_MALWARE_FAILURE },
            error(`There was an error removing the Malware term ${name}.`),
          ]);
        });
    });

export default combineEpics(
  viewLoadedEpic,
  fetchMalwareEpic,
  saveMalwareEpic,
  deleteMalwareEpic,
);



// WEBPACK FOOTER //
// ./src/app/modules/malware/epics.js